<template>
  <b-card
    v-if="engineer"
    class="engineer-details-card"
  >
    <!-- Header -->
    <div class="engineer-header d-flex align-items-center mb-1">
      <div class="engineer-avatar mr-1 d-flex align-items-center justify-content-center">
        <feather-icon
          icon="UserIcon"
          :size="'24'"
        />
      </div>
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-25">
            {{ engineer.name }}
          </h4>
          <div class="text-muted small">
            <span v-if="engineer.job_description">{{ engineer.job_description }}</span>
            <span v-if="engineer.department_name">
              • {{ engineer.department_name }}
            </span>
          </div>
        </div>
        <b-spinner
          v-if="loading"
          small
          class="ml-1"
        />
      </div>
    </div>

    <!-- Section 1: Engineer Information -->
    <div v-if="section === 'engineer-info'">
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <div class="label-with-icon">
            <feather-icon
              icon="MailIcon"
              :size="'14'"
              class="mr-25 text-primary"
            />
            <span class="label-title">Email</span>
          </div>
          <div class="label-value">
            {{ engineer.email }}
          </div>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <div class="label-with-icon">
            <feather-icon
              icon="PhoneIcon"
              :size="'14'"
              class="mr-25 text-primary"
            />
            <span class="label-title">Phone</span>
          </div>
          <div class="label-value">
            {{ engineer.phone }}
          </div>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <div class="label-with-icon">
            <feather-icon
              icon="BriefcaseIcon"
              :size="'14'"
              class="mr-25 text-primary"
            />
            <span class="label-title">Department Name</span>
          </div>
          <div class="label-value">
            {{ engineer.department_name }}
          </div>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <div class="label-with-icon">
            <feather-icon
              icon="CalendarIcon"
              :size="'14'"
              class="mr-25 text-primary"
            />
            <span class="label-title">Joined At</span>
          </div>
          <div class="label-value">
            {{ engineer.joined_at }}
          </div>
        </b-col>

        <b-col
          cols="12"
          class="mb-1"
        >
          <div class="label-with-icon">
            <feather-icon
              icon="ActivityIcon"
              :size="'14'"
              class="mr-25 text-primary"
            />
            <span class="label-title">Load</span>
          </div>
          <div class="label-value">
            {{ engineer.load }}
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Section 2: Status & Office (with Schedule + Courses) -->
    <div v-else-if="section === 'status-office'">
      <b-row>
        <!-- Status -->
        <b-col
          v-if="engineer.status && engineer.status.length"
          cols="12"
          md="6"
          class="mb-1"
        >
          <h6 class="section-title d-flex align-items-center mb-25">
            <feather-icon
              icon="AlertCircleIcon"
              :size="'15'"
              class="mr-50 text-primary"
            />
            <span>Permissions</span>
          </h6>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(state, index) in engineer.status"
              :key="index"
              class="small mb-25 d-flex align-items-center"
            >
              <b-badge
                variant="light-primary"
                class="mr-50"
              >
                {{ index + 1 }}
              </b-badge>
              <span>{{ state.status }}</span>
            </li>
          </ul>
        </b-col>

        <!-- Office -->
        <b-col
          v-if="engineer.office"
          cols="12"
          md="6"
          class="mb-1"
        >
          <h6 class="section-title d-flex align-items-center mb-25">
            <feather-icon
              icon="MapPinIcon"
              :size="'15'"
              class="mr-50 text-primary"
            />
            <span>Office</span>
          </h6>
          <div class="small">
            <div v-if="engineer.office.name">
              <strong>Office Name:</strong> {{ engineer.office.name }}
            </div>
            <div v-if="engineer.office.country">
              <strong>Office Country:</strong> {{ engineer.office.country }}
            </div>
          </div>
        </b-col>

        <!-- Courses (99–135) -->
        <b-col
          v-if="engineer.courses && engineer.courses.length"
          cols="12"
          class="mt-1"
        >
          <h6 class="section-title d-flex align-items-center mb-25">
            <feather-icon
              icon="BookOpenIcon"
              :size="'15'"
              class="mr-50 text-primary"
            />
            <span>Courses</span>
          </h6>
          <div class="courses-list">
            <div
              v-for="(course, index) in engineer.courses"
              :key="index"
              class="course-item"
            >
              <div class="font-weight-bold small">
                {{ course.name_ar }} - {{ course.name_en }}
              </div>
              <div class="text-muted small">
                {{ course.description_ar }} - {{ course.description_en }}
              </div>
              <div class="small mt-25">
                <span v-if="course.issuer">
                  <strong>Issuer:</strong> {{ course.issuer }}
                </span>
                <span v-if="course.expired_at">
                  • <strong>Expired At:</strong> {{ course.expired_at }}
                </span>
                <span v-if="course.doc && course.doc.url">
                  •
                  <a
                    :href="course.doc.url"
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadCloudIcon"
                      :size="'14'"
                    />
                  </a>
                </span>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Section 3: Certificates Groups -->
    <div v-else-if="section === 'certificates'">
      <div
        v-if="engineer.certificates_groups && engineer.certificates_groups.length"
        class="cert-groups"
      >
        <div
          v-for="(group, index) in engineer.certificates_groups"
          :key="index"
          class="cert-group-card"
        >
          <div class="d-flex justify-content-between align-items-center mb-25">
            <div>
              <div class="font-weight-bold small">
                {{ group.name.en }}
              </div>
              <div class="text-muted small">
                {{ group.name.ar }}
              </div>
            </div>
            <b-badge variant="light-primary">
              {{ group.certificates.length }} certificates
            </b-badge>
          </div>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(certificate, certIndex) in group.certificates"
              :key="certIndex"
              class="small mb-25"
            >
              <feather-icon
                icon="CheckCircleIcon"
                :size="'14'"
                class="mr-25 text-success"
              />
              <strong>{{ certificate.name.en }}</strong>
              <span class="text-muted">
                ({{ certificate.name.ar }})
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-else
        class="text-muted small"
      >
        No certificates groups found for this engineer.
      </div>
    </div>
  </b-card>
</template>

<script>
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  components: { FeatherIcon },
  props: {
    engineer: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    section: {
      type: String,
      default: 'engineer-info',
    },
  },
}
</script>

<style scoped>
.engineer-details-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 24px rgba(25, 42, 70, 0.12);
}

.engineer-header .engineer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f3f4fb;
  color: #5e50ee;
}

.engineer-tabs .nav-link {
  border-radius: 999px 999px 0 0;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
}

.engineer-tabs .nav-link.active {
  background-color: #7367f0 !important;
  color: #ffffff !important;
}

.label-with-icon {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #6c757d;
}

.label-title {
  font-weight: 600;
}

.label-value {
  font-size: 0.85rem;
}

.section-title {
  font-weight: 600;
}

.schedule-list .schedule-item,
.courses-list .course-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
  background: #ffffff;
}

.cert-groups .cert-group-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.5rem;
  background: #ffffff;
}
</style>
